/**
 * Route guard for surfaces whose backend routes are gated on
 * `require_module_admin(module)`.
 *
 * The admit test is `useIsModuleAdmin` — the single frontend mirror of that
 * dependency's three-way check (tenant admin / `{module}.settings.manage` role /
 * T11 per-module access level `admin`). Re-typing the check here is how it
 * drifted before: this guard used to test only the first two, so a user granted
 * the per-module `admin` access level was bounced off a page the API would have
 * served them.
 *
 * The identifier the URL carries is NOT the one that check keys on. The segment
 * is a permission-module name (or a bare routing slug), while
 * `module_access_levels` is keyed by module config **id** — which is why
 * `/admin/module-settings/products` bounced a user holding `master_data: 'admin'`,
 * the only grant the per-user module-access UI can give. Resolve the segment
 * through `getModuleIdForPermissionModuleName` before handing it to the hook.
 *
 * A denied viewer gets a stated permission page, not a silent redirect. The
 * page it guards would otherwise mount, fire its reads, and render "Failed to
 * load data" per widget once the 403s land — a load failure where the honest
 * answer is an access one.
 */

import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShieldAlert } from 'lucide-react';
import { useAuth } from '../../shared/auth';
import { useIsModuleAdmin } from '../../shared/hooks';
import {
  getModuleById,
  getModuleIdForPermissionModuleName,
} from '../../shared/config/modules.config';
import { PageContainer, EmptyState } from '../../shared/components/common';

/**
 * URL slugs that are neither a permission-module name nor a module id.
 *
 * These come from the module's `tenantSettingsPath`, so they are pure routing
 * spellings — they must NOT go in the config's `permissionModules`, which declares
 * the names a `require_module_admin(...)` gate may legitimately use.
 *
 * **Every `/admin/module-settings/:moduleId` slug must land on a real module
 * through this map.** A slug that resolves to nothing is not a loud failure: both
 * grant lookups simply read a key nothing ever writes, so the page admits tenant
 * admins and silently turns away exactly the per-module admins it exists for.
 * `contacts` did that until WI f32d0216. `tools/check_module_settings_slug_resolves.py`
 * now fails the build on the next one.
 */
export const URL_SLUG_TO_PERMISSION: Record<string, string> = {
  work: 'tasks',
  ai: 'ai_agents',
  chat: 'ai_chat',
  // master_data's two settings areas — the catalog and contacts/organizations —
  // need no entry: it is administered in two SUB-SCOPES whose names (`products`,
  // `contacts`) are its `permissionModules`, and each area's slug already IS its
  // scope's name, so the identity fallback below is correct and scope-exact. A
  // "Products Admin" is therefore turned away from `/admin/module-settings/contacts`
  // — which is the point of the split (WI 801c4d7f).
};

interface ModuleAdminRouteProps {
  children: React.ReactNode;
  /**
   * Explicit permission module name. Use on static settings routes that have no
   * `:moduleId` URL param (e.g. `/procurement/settings`); falls back to the URL
   * param for the canonical `/admin/module-settings/:moduleId` route.
   */
  module?: string;
}

export function ModuleAdminRoute({ children, module }: ModuleAdminRouteProps) {
  const { t, i18n } = useTranslation('common');
  const { user } = useAuth();
  const { moduleId } = useParams<{ moduleId: string }>();

  // Three spellings, resolved in order: routing slug → permission-module name →
  // config id, which is what both `module_access_levels` and the module registry
  // are keyed by.
  const resolvedSlug = module ?? moduleId ?? '';
  const permissionModule = URL_SLUG_TO_PERMISSION[resolvedSlug] ?? resolvedSlug;
  const configModuleId = getModuleIdForPermissionModuleName(permissionModule);
  // The PERMISSION name, not the config id: a settings page is one module-admin
  // SCOPE, and asking the module-wide question would admit a holder of master_data's
  // other scope to a page the backend gate answers 403 to. For a single-scope module
  // the two arguments mean the same thing. The config id is still what names the
  // module in the copy below.
  const isModuleAdmin = useIsModuleAdmin(permissionModule);

  // No session at all — these routes sit behind ProtectedRoute, so this is the
  // unauthenticated edge case rather than a permission one.
  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (isModuleAdmin) {
    return <>{children}</>;
  }

  // The module registry owns the display-name key (it is NOT `modules.{id}` for
  // every module — `software_factory` → `modules.softwareFactory`), so resolve it
  // there rather than reconstructing it. An unknown id falls back to generic copy
  // rather than printing a raw key at the user.
  const nameKey = getModuleById(configModuleId)?.nameKey;
  const moduleName = nameKey && i18n.exists(`common:${nameKey}`) ? t(nameKey) : null;

  return (
    <PageContainer maxWidth="lg">
      <EmptyState
        icon={ShieldAlert}
        title={t('app.moduleAdminRequiredTitle')}
        message={
          moduleName
            ? t('app.moduleAdminRequiredMessage', { module: moduleName })
            : t('app.moduleAdminRequiredMessageGeneric')
        }
      />
    </PageContainer>
  );
}
